import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import DarkIconButton from '@/components/ui/Buttons/SecondaryIconButton'
import { useLanguage } from '@/context/LanguageContext'
import {
  CreateInvoiceReq,
  ProductType
} from '@/services/mobi-api/invoice/types'
import useIntl from '@/utils/intl'
import { TextField, Dialog, Checkbox } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import React, { FunctionComponent, useEffect, useReducer } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'
import { useMobiApi } from '@/services/mobi-api'
import { useToast } from '@/context/ToastContext'
import Loader from '@/components/ui/loader'
import MCButton from '@/components/ui/Buttons/MobicomButton'
import { IMG_URL } from '@/utils/constants'
import { getSystemEnv } from '@/utils/env'
import { useAuth } from '@/context/AuthContext'
import { isMobile } from '@/utils/helpers'
import { DanResponse } from '@/services/mobi-api/handsetDti/types'
import SuccessZMS from '@/pages/successZMS'
import { isEmptyEmail, isEmptyString } from '@/utils/validations'

export interface Obj {
  instanceId?: string
  email?: string
  phoneNumber?: string
  choosenPackage?: string
  rateplan?: string
  danUrl?: string
}

interface PaymentInfoProps {
  onClick: (obj: Obj) => void
  onClose: () => void
}

interface State {
  corpId?: string
  checked: boolean
  vatType: 'CONSUMER' | 'CORPORATE'
  loading: boolean
  openTerms: boolean
  instance?: DanResponse
  id?: string
  emailInput: string
  errorType?: 'BLACKLIST' | 'UNDERAGE' | 'VERIFICATION_FAILED'
}

const PaymentInfo: FunctionComponent<PaymentInfoProps> = ({
  onClick,
  onClose
}) => {
  const router = useRouter()
  const { isWebView } = useAuth()
  const { locale } = useLanguage()
  const { handset, invoice } = useMobiApi()
  const { showError } = useToast()
  const searchParams = useSearchParams()
  const [terms, openTerms] = React.useState(false)
  const [isSuccess, setIsSuccess] = React.useState(false)
  const { translate } = useIntl()
  const [state, setState] = useReducer(
    (state: State, newState: Partial<State>) => ({ ...state, ...newState }),
    {
      corpId: '',
      checked: false,
      vatType: 'CONSUMER',
      loading: true,
      openTerms: false,
      emailInput: '',
      errorType: undefined
    }
  )

  useEffect(() => {
    handleQuery()
  }, [searchParams])

  useEffect(() => {
    const initialEmailFromChild = searchParams.get('initialEmail')
    if (!isEmptyEmail(initialEmailFromChild)) {
      setState({
        emailInput: initialEmailFromChild ?? ''
      })
    } else {
      if (!isEmptyEmail(state.instance?.email)) {
        setState({
          emailInput: state.instance?.email ?? ''
        })
      }
    }
    if (
      (state.instance != null) && !state.instance?.configuratorRes?.result?.price?.totalAmount
    ) {
      showError(translate('error.configuratorDataMissing'))
      setState({ loading: false })
    } else if (state.instance != null) {
      setState({ loading: false })
    }
  }, [searchParams, state.instance])

  const handleQuery = (): void => {
    const _id = searchParams.get('_id')?.trim()
    setState({ id: _id ?? '' })
    if (!isEmptyString(_id)) {
      void fetchRequest(_id ?? '')
    } else {
      showError(translate('request.failed'))
      setState({ loading: false })
    }
  }

  const fetchRequest = async (_id: string): Promise<void> => {
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out')), 10000)
      )
      const res = await Promise.race([
        handset.getHandsetDti(_id),
        timeoutPromise
      ])
      if (res !== undefined && (res as DanResponse).error === undefined) {
        const instance = res as DanResponse
        if (!instance.configuratorVerified) {
          setState({
            errorType: 'VERIFICATION_FAILED',
            loading: false
          })
        } else {
          setState({
            instance,
            errorType: undefined,
            loading: false
          })
        }
      } else {
        const errorRes = res as {
          error?: string
          cause?: { messageEn?: string, messageMn?: string }
        }
        showError(
          locale === 'en'
            ? errorRes?.cause?.messageEn ??
                errorRes?.error ??
                translate('request.failed')
            : errorRes?.cause?.messageMn ??
                errorRes?.error ??
                translate('request.failed')
        )
        setState({ loading: false })
      }
    } catch (e) {
      showError(translate('request.failed'))
      setState({ loading: false })
    }
  }

  const VAT = [
    {
      name: translate('shop.vatCons'),
      icon: '/uploads/icons/user1.svg',
      type: 'CONSUMER'
    },
    {
      name: translate('shop.vatCorp'),
      icon: '/uploads/icons/briefcase.svg',
      type: 'CORPORATE'
    }
  ]

  const handleChangeVatType = (value: string): void => {
    setState({ vatType: value as 'CONSUMER' | 'CORPORATE' })
  }

  const handleChangeCheckbox = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setState({ checked: event.target.checked })
  }

  const closeModal = (): void => {
    setState({ checked: true })
    openTerms(false)
  }

  const handleEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setState({ emailInput: event.target.value })
  }

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const isValid = emailRegex.test(email.trim())
    return isValid
  }

  const handleContinue = async (): Promise<void> => {
    setState({ loading: true })
    if (!validateEmail(state.emailInput)) {
      showError(translate('error'))
      setState({ loading: false })
      return
    }
    const payload: CreateInvoiceReq = {
      isdn: state.instance?.isdn,
      products: [
        {
          productType: ProductType.HANDSETDTI,
          price: state.instance?.configuratorRes?.result?.price?.totalAmount,
          quantity: 1,
          productCode: state.id ?? ''
        }
      ],
      customerType: state.vatType === 'CORPORATE' ? 'CORPORATE' : 'CONSUMER',
      languageCode: locale,
      vatEmail: state.emailInput,
      corpId: state.vatType === 'CORPORATE' ? state.corpId : undefined,
      device: isMobile(),
      redirectUrl:
        getSystemEnv().DOMAIN + '/successZMS?id=' + (state.instance?._id ?? '')
    }
    try {
      const res = await invoice.create(payload)
      if (
        res?.invoiceId === null ||
        res?.invoiceId === undefined ||
        res?.invoiceId === ''
      ) {
        showError(translate('error'))
      } else {
        if (isWebView) {
          const data = {
            key: 'invoice',
            value: {
              command: 'handsetdti',
              identifier: state.instance?.isdn,
              product: state.instance?._id,
              email: state.emailInput,
              amount:
                state.instance?.configuratorRes?.result?.price?.totalAmount,
              register: state.vatType === 'CORPORATE' ? state.corpId : '',
              vatType: state.vatType === 'CORPORATE' ? 'CORP' : 'CONS',
              activationCode: res?.invoiceId,
              productName: translate('quick.leasing')
            }
          }
          let webView
          if ((window as any).mobiBridge !== undefined) {
            webView = (window as any).mobiBridge
          }
          if ((window as any).ReactNativeWebview !== undefined) {
            webView = (window as any).ReactNativeWebview
          }
          if (webView !== undefined) {
            webView.postMessage(JSON.stringify(data))
          }
        } else if (res?.redirectUrl !== undefined && res.redirectUrl !== '') {
          router.push(res.redirectUrl)
        } else {
          showError(translate('error'))
        }
      }
    } catch (e) {
      console.error(e)
    }
  }

  const disableContinue = (): boolean => {
    const isDisabled =
      (state.vatType === 'CORPORATE' && (state.corpId?.length ?? 0) < 7) ||
      !state.checked ||
      !validateEmail(state.emailInput)
    return isDisabled
  }

  if (isSuccess) {
    return (
      <SuccessZMS
        onClose={() => {
          setIsSuccess(false)
          onClose()
        }}
        instanceId={state.instance?._id}
      />
    )
  }

  if (state.loading) {
    return (
      <div className='w-full h-96 flex items-center justify-center'>
        <Loader />
      </div>
    )
  }

  return (
    <div className='bg-surfaceInverse rounded-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col overflow-auto'>
      <div className='flex-grow overflow-auto pb-36'>
        <div className='md:p-8 p-4 flex flex-col gap-4 border-b border-secondaryMidStroke'>
          <span className='text-textMid text-xs md:text-sm font-bold'>
            {translate('quick.leasing')}
          </span>
          <div className='py-2 bg-surfaceWebGrey border border-secondaryMidStroke w-full rounded-xl'>
            <div className='flex justify-between px-4 py-2'>
              <span className='text-textMid text-xs md:text-sm font-medium'>
                {translate('phoneNumber')}:
              </span>
              <span className='text-textHigh text-xs md:text-sm font-bold'>
                {state.instance?.isdn && state.instance.isdn !== 'empty'
                  ? state.instance.isdn
                  : '-'}
              </span>
            </div>
            <div className='flex justify-between px-4 py-2'>
              <span className='text-textMid text-xs md:text-sm font-medium'>
                {translate('price')}:
              </span>
              <span className='text-textHigh text-xs md:text-sm font-bold'>
                {state.instance?.configuratorRes?.result?.price?.totalAmount
                  ? `${state.instance.configuratorRes.result.price.totalAmount}₮`
                  : '-'}
              </span>
            </div>
          </div>
        </div>

        <div className='md:p-8 p-4'>
          <span className='text-textMid text-xs md:text-sm font-bold'>
            {translate('shop.vat')}
          </span>
          <div className='flex gap-4 mt-4 w-full md:w-96'>
            {VAT.map((vat) => (
              <MCButton
                type='shop'
                selected={vat.type === state.vatType}
                fullwidth
                size='sm'
                onClick={() => handleChangeVatType(vat.type)}
                key={vat.type}
                leadingIconImg={IMG_URL + vat.icon}
              >
                {vat.name}
              </MCButton>
            ))}
          </div>
          <div className='md:w-96 w-full'>
            <div className='relative'>
              <TextField
                label={
                  state.vatType === 'CORPORATE'
                    ? translate('shop.corpEmail')
                    : translate('shop.vatEmail')
                }
                variant='outlined'
                className='w-full rounded-xl border-secondaryHighStroke bg-surfaceInverse text-textHigh mt-6'
                value={state.emailInput}
                onChange={handleEmailChange}
                InputLabelProps={{
                  style: { color: 'var(--color-text-textHigh)' }
                }}
                InputProps={{
                  style: {
                    color: 'var(--color-text-textHigh)'
                  }
                }}
                sx={{
                  '& fieldset': {
                    borderRadius: '8px'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'var(--color-text-secondaryHighStroke)'
                  }
                }}
              />
            </div>
            {state.vatType === 'CORPORATE' && (
              <TextField
                type='string'
                value={state.corpId}
                label={translate('shop.vatCorpId')}
                variant='outlined'
                className='w-full rounded-xl border-secondaryHighStroke bg-surfaceInverse text-textHigh mt-6'
                onChange={(e) => {
                  setState({ corpId: e.target.value })
                }}
                InputLabelProps={{
                  style: { color: 'var(--color-text-textHigh)' }
                }}
                InputProps={{
                  style: {
                    color: 'var(--color-text-textHigh)'
                  }
                }}
                sx={{
                  '& fieldset': {
                    borderRadius: '8px'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'var(--color-text-secondaryHighStroke)'
                  }
                }}
              />
            )}
          </div>
          <div className='flex items-center mt-10'>
            <Checkbox
              color='error'
              checked={state.checked}
              onChange={handleChangeCheckbox}
              style={{
                cursor: 'pointer',
                marginRight: '8px',
                width: '20px',
                height: '20px'
              }}
              aria-label={translate('acceptTermsOfService')}
            />
            <p
              className='text-xs md:text-sm font-medium cursor-pointer hover:underline hover:underline-offset-4 hover:text-[#EF4444] text-textHigh'
              onClick={() => openTerms(true)}
            >
              {translate('acceptTermsOfService')}
            </p>
          </div>
        </div>
      </div>
      <Dialog open={terms}>
        <div className='p-4 bg-surfaceMid'>
          <div className='flex justify-between items-center w-full'>
            <span className='text-textMid font-bold text-lg'>
              {translate('termsOfService')}
            </span>
            <DarkIconButton
              type='text'
              size='sm'
              icon={<CloseIcon fontSize='small' />}
              onClick={() => openTerms(false)}
            />
          </div>
          <div className='py-8 px-10 border border-slate-200 rounded-lg mt-4'>
            <p className='text-lg font-bold text-textHigh'>
              {translate('generalProvs')}
            </p>
            <p className='text-sm text-textMid font-medium text-justify mt-6'>
              {translate('termMain')}
              <br />
              1. {translate('term1')}
              <br />
              2. {translate('term2')}
            </p>
          </div>
          <div className='mt-4 w-full flex justify-end'>
            <MCButton size='sm' onClick={closeModal}>
              {translate('accept')}
            </MCButton>
          </div>
        </div>
      </Dialog>
      <div className='fixed md:sticky bottom-0 right-0 left-0 z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
        <DarkButton
          fullwidth
          disabled={disableContinue()}
          onClick={() => {
            void handleContinue()
          }}
        >
          {translate('continue')}
        </DarkButton>
      </div>
    </div>
  )
}

export default PaymentInfo
